// Master User Roles & Permissions System

export type MasterRole = 
  | 'PLATFORM_OWNER' 
  | 'OPERATIONS_OFFICER' 
  | 'BUSINESS_MANAGER';

export interface RoleDefinition {
  id: MasterRole;
  title: string;
  accessLevel: 'FULL' | 'LIMITED' | 'OPERATIONAL';
  permissions: {
    // Platform Management
    managePlatformConfig?: boolean;
    manageSystemGovernance?: boolean;
    manageUserRoles?: boolean;
    
    // Event Management
    createEvents?: boolean;
    editEvents?: boolean;
    deleteEvents?: boolean;
    approveEvents?: boolean;
    
    // Financial Access
    viewFinancialReports?: boolean;
    viewRevenueTracking?: boolean;
    viewFinancialDashboards?: boolean;
    
    // Client Management
    viewClientReports?: boolean;
    viewClientAnalytics?: boolean;
    manageClientProfiles?: boolean;
    
    // Database Access
    databaseAccess?: boolean;
    databaseAdmin?: boolean;
    
    // Operational
    issueTickets?: boolean;
    issueEventPasses?: boolean;
    operationalSupport?: boolean;
    
    // Analytics
    viewAnalytics?: boolean;
    viewEventAnalytics?: boolean;
    viewTicketSales?: boolean;
    viewClientPerformance?: boolean;
    
    // View-Only Access
    viewConsumerDatabase?: boolean;
    viewUserDatabase?: boolean;
    viewOperationalDashboards?: boolean;
  };
  restrictions: string[];
  description: string;
}

export const MASTER_ROLES: Record<MasterRole, RoleDefinition> = {
  PLATFORM_OWNER: {
    id: 'PLATFORM_OWNER',
    title: 'Co-Owner / Super Administrator',
    accessLevel: 'FULL',
    permissions: {
      managePlatformConfig: true,
      manageSystemGovernance: true,
      manageUserRoles: true,
      createEvents: true,
      editEvents: true,
      deleteEvents: true,
      approveEvents: true,
      viewFinancialReports: true,
      viewRevenueTracking: true,
      viewFinancialDashboards: true,
      viewClientReports: true,
      viewClientAnalytics: true,
      manageClientProfiles: true,
      databaseAccess: true,
      databaseAdmin: true,
      issueTickets: true,
      issueEventPasses: true,
      operationalSupport: true,
      viewAnalytics: true,
      viewEventAnalytics: true,
      viewTicketSales: true,
      viewClientPerformance: true,
      viewConsumerDatabase: true,
      viewUserDatabase: true,
      viewOperationalDashboards: true,
    },
    restrictions: [],
    description: 'Full access to all platform modules and functionalities. Platform configuration, system governance, and policy enforcement. Creation, modification, and revocation of user roles and permissions.'
  },
  
  OPERATIONS_OFFICER: {
    id: 'OPERATIONS_OFFICER',
    title: 'Operations & Database Access Officer',
    accessLevel: 'LIMITED',
    permissions: {
      databaseAccess: true,
      issueTickets: true,
      issueEventPasses: true,
      operationalSupport: true,
      viewOperationalDashboards: true,
    },
    restrictions: [
      'No IT system architecture or technical backend configurations',
      'No creation, editing, or deletion of events',
      'No financial reporting or revenue analytics',
      'No client financial or performance reporting',
      'No authority to make system, financial, or structural changes'
    ],
    description: 'Access to the platform database strictly for operational purposes. Ability to issue tickets and event passes. Operational support for ticketing and customer fulfillment processes.'
  },
  
  BUSINESS_MANAGER: {
    id: 'BUSINESS_MANAGER',
    title: 'Platform & Business Manager',
    accessLevel: 'OPERATIONAL',
    permissions: {
      createEvents: true,
      editEvents: true,
      manageClientProfiles: true,
      viewFinancialReports: true,
      viewRevenueTracking: true,
      viewFinancialDashboards: true,
      viewClientReports: true,
      viewClientAnalytics: true,
      viewAnalytics: true,
      viewEventAnalytics: true,
      viewTicketSales: true,
      viewClientPerformance: true,
      viewConsumerDatabase: true,
      viewUserDatabase: true,
      viewOperationalDashboards: true,
    },
    restrictions: [
      'No data management or database administration',
      'No modification of consumer or user databases',
      'No backend system or technical configurations'
    ],
    description: 'Create, edit, and manage events on the platform. Access financial reports, revenue summaries, and event-level financial insights. Access and manage client profiles and client-related information.'
  }
};

export const getRoleDefinition = (role: MasterRole): RoleDefinition => {
  return MASTER_ROLES[role];
};

export const hasPermission = (role: MasterRole, permission: keyof RoleDefinition['permissions']): boolean => {
  const roleDef = getRoleDefinition(role);
  return roleDef.permissions[permission] === true;
};

export const getRoleDisplayName = (role: MasterRole): string => {
  return MASTER_ROLES[role]?.title || role;
};

export const getRoleAccessLevel = (role: MasterRole): string => {
  return MASTER_ROLES[role]?.accessLevel || 'UNKNOWN';
};

